/**
 * ShareTasteInsightsModal
 *
 * Modal with options to share TasteInsights:
 * - Copy shareable link
 * - Download as image
 * - Native share sheet (on supported platforms)
 */
import { useState, useRef, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Button,
  Text,
  Box,
  useToast,
  Spinner,
  Divider,
  Icon,
} from '@chakra-ui/react'
import { Link2, Download, Share2, Check, Image, Copy } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { ShareableTasteBadge, ShareableTasteBadgeMini } from './ShareableTasteBadge'
import {
  generateTasteInsightsBadgeImage,
  downloadShareImage,
  shareImageNatively,
  isNativeShareSupported,
} from '@/utils/shareImageGenerator'
import { haptics } from '@/utils/haptics'
import { FavoriteCuisine } from '@/utils/cuisineColorMapping'
import type { TasteInsights } from '@/types/tasteInsights'
import { analytics } from '@/lib/analytics'
import { AnalyticsEvents } from '@/lib/analytics/events'

const MotionBox = motion.create(Box)

export interface ShareTasteInsightsModalProps {
  isOpen: boolean
  onClose: () => void
  tasteInsights: TasteInsights
  userName: string
  favoriteCuisines?: FavoriteCuisine[]
  /** User ID for generating shareable URL */
  userId: number
}

type ShareAction = 'idle' | 'copying' | 'downloading' | 'sharing'
type ShareSuccess = 'link' | 'download' | 'share' | null

export function ShareTasteInsightsModal({
  isOpen,
  onClose,
  tasteInsights,
  userName,
  favoriteCuisines = [],
  userId,
}: ShareTasteInsightsModalProps) {
  const toast = useToast()
  const badgeRef = useRef<HTMLDivElement>(null)
  const [action, setAction] = useState<ShareAction>('idle')
  const [successAction, setSuccessAction] = useState<ShareSuccess>(null)

  const canShare = isNativeShareSupported()

  // Generate shareable URL for the profile with TasteInsights
  const getShareUrl = useCallback(() => {
    const baseUrl = window.location.origin
    return `${baseUrl}/profile/${userId}?showTasteInsights=true`
  }, [userId])

  // Copy link to clipboard
  const handleCopyLink = async () => {
    setAction('copying')
    await haptics.spring()

    try {
      const url = getShareUrl()
      await navigator.clipboard.writeText(url)
      setSuccessAction('link')
      await haptics.celebrate()

      analytics.capture(AnalyticsEvents.TASTE_PROFILE_SHARED, {
        share_method: 'link',
        user_id: userId,
      })

      toast({
        title: 'Link copied!',
        description: 'Share your TasteInsights with friends',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      // Reset success state after animation
      setTimeout(() => setSuccessAction(null), 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
      await haptics.error()
      toast({
        title: 'Failed to copy link',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setAction('idle')
    }
  }

  // Download as image
  const handleDownloadImage = async () => {
    setAction('downloading')
    await haptics.spring()

    try {
      const blob = await generateTasteInsightsBadgeImage(
        tasteInsights,
        userName,
        favoriteCuisines
      )

      await downloadShareImage(blob, `${userName}-taste-insights.png`)
      setSuccessAction('download')
      await haptics.celebrate()

      analytics.capture(AnalyticsEvents.TASTE_PROFILE_SHARED, {
        share_method: 'download',
        user_id: userId,
      })

      toast({
        title: 'Image downloaded!',
        description: 'Ready to share on social media',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      setTimeout(() => setSuccessAction(null), 2000)
    } catch (error) {
      console.error('Failed to download image:', error)
      await haptics.error()
      toast({
        title: 'Failed to create image',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setAction('idle')
    }
  }

  // Native share
  const handleNativeShare = async () => {
    if (!canShare) return

    setAction('sharing')
    await haptics.spring()

    try {
      const blob = await generateTasteInsightsBadgeImage(
        tasteInsights,
        userName,
        favoriteCuisines
      )

      const shared = await shareImageNatively(
        blob,
        `${userName}'s TasteInsights`,
        `Check out my TasteInsights™ - I'm "${tasteInsights.tasteTribe}"! ${tasteInsights.tasteTribeEmojis}`
      )

      if (shared) {
        setSuccessAction('share')
        await haptics.celebrate()

        analytics.capture(AnalyticsEvents.TASTE_PROFILE_SHARE_THIRD_PARTY_CLICKED, {
          platform: 'native',
          user_id: userId,
        })

        setTimeout(() => setSuccessAction(null), 2000)
      }
    } catch (error) {
      console.error('Failed to share:', error)
      await haptics.error()
      toast({
        title: 'Failed to share',
        description: 'Please try downloading the image instead',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setAction('idle')
    }
  }

  const isLoading = action !== 'idle'

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        bg="var(--card-bg)"
        borderRadius="var(--r-card)"
        mx={4}
        maxH="85vh"
        overflow="hidden"
      >
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="var(--card-surface-2)" borderRadius="var(--r-inner)" p={2}>
              <Share2 size={20} color="var(--chakra-colors-blue-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Share Your TasteInsights
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                Show off your unique flavor profile
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="var(--card-text-2)" _hover={{ bg: 'var(--card-surface-2)' }} />

        <ModalBody
          pb={6}
          // Scrollbar styling: the global ghost-scrollbar rule in
          // src/index.css covers this container — no local override needed.
        >
          <VStack spacing={5}>
            {/* Preview */}
            <Box
              borderRadius="var(--r-card-compact)"
              overflow="hidden"
              border="1px solid"
              borderColor="var(--card-divider)"
              boxShadow="0 4px 16px rgba(0, 0, 0, 0.15)"
            >
              <ShareableTasteBadgeMini
                tasteInsights={tasteInsights}
                favoriteCuisines={favoriteCuisines}
              />
            </Box>

            <Divider borderColor="var(--card-divider)" />

            {/* Share Options */}
            <VStack spacing={3} width="100%">
              {/* Copy Link */}
              <ShareButton
                icon={successAction === 'link' ? Check : Link2}
                label={successAction === 'link' ? 'Link Copied!' : 'Copy Shareable Link'}
                isLoading={action === 'copying'}
                isSuccess={successAction === 'link'}
                onClick={handleCopyLink}
                disabled={isLoading}
              />

              {/* Download Image */}
              <ShareButton
                icon={successAction === 'download' ? Check : Download}
                label={successAction === 'download' ? 'Downloaded!' : 'Download Image'}
                isLoading={action === 'downloading'}
                isSuccess={successAction === 'download'}
                onClick={handleDownloadImage}
                disabled={isLoading}
              />

              {/* Native Share (only on supported platforms) */}
              {canShare && (
                <ShareButton
                  icon={successAction === 'share' ? Check : Share2}
                  label={successAction === 'share' ? 'Shared!' : 'Share'}
                  isLoading={action === 'sharing'}
                  isSuccess={successAction === 'share'}
                  onClick={handleNativeShare}
                  disabled={isLoading}
                  variant="primary"
                />
              )}
            </VStack>

            {/* Hidden badge for image generation */}
            <Box
              position="absolute"
              left="-9999px"
              top="-9999px"
              aria-hidden="true"
            >
              <ShareableTasteBadge
                ref={badgeRef}
                tasteInsights={tasteInsights}
                userName={userName}
                favoriteCuisines={favoriteCuisines}
                size={1080}
              />
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

/**
 * Individual share button with loading and success states
 */
function ShareButton({
  icon: IconComponent,
  label,
  description,
  isLoading,
  isSuccess,
  onClick,
  disabled,
  variant = 'default',
}: {
  icon: typeof Link2
  label: string
  description?: string
  isLoading: boolean
  isSuccess: boolean
  onClick: () => void
  disabled: boolean
  variant?: 'default' | 'primary'
}) {
  const isPrimary = variant === 'primary'

  return (
    <Button
      width="100%"
      height="auto"
      py={4}
      px={5}
      bg={isPrimary ? 'blue.500' : 'var(--card-surface-2)'}
      color={isPrimary ? 'white' : 'var(--card-text)'}
      borderRadius="var(--r-card-compact)"
      borderWidth={isPrimary ? 0 : '1px'}
      borderColor="var(--card-divider)"
      _hover={{
        bg: isPrimary ? 'blue.600' : 'var(--card-divider)',
        transform: 'translateY(-1px)',
      }}
      _active={{
        transform: 'translateY(0)',
      }}
      onClick={onClick}
      isDisabled={disabled && !isLoading}
      transition="all 0.2s"
    >
      <HStack width="100%" justify="space-between">
        <HStack spacing={3}>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <MotionBox
                key="loading"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <Spinner size="sm" color={isPrimary ? 'white' : 'blue.500'} />
              </MotionBox>
            ) : (
              <MotionBox
                key="icon"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <Icon
                  as={IconComponent}
                  boxSize={5}
                  color={isSuccess ? 'green.500' : isPrimary ? 'white' : 'blue.500'}
                />
              </MotionBox>
            )}
          </AnimatePresence>
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold" fontSize="md">
              {label}
            </Text>
            {description && (
              <Text fontSize="xs" color={isPrimary ? 'whiteAlpha.800' : 'var(--card-text-3)'}>
                {description}
              </Text>
            )}
          </VStack>
        </HStack>
      </HStack>
    </Button>
  )
}
